import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import { View } from 'react-native';
import { Text } from '@dbk/ui-text'
import { Chart, Grid, LineChart, XAxis, YAxis } from '@dbk/ui-charts';
import { createContainer } from '@dbk-legacy/muster/react';
import { ref } from '@dbk-legacy/muster/utils';

import sortBy from 'lodash/sortBy';
import DateFns from 'date-fns';

const {
  format,
  subDays,
  subMonths,
  startOfMonth,
  endOfMonth,
  getDaysInMonth,
} = DateFns;

import {flow} from 'lodash/fp'
import { PALETTE, NUMBER_SCALE } from '../../../../constants';
import Interactions from './interactions';
import NoDataChart from '../../../../components/common/no-data-chart';

import styles, { axisSVGStyles } from './portfolio-time-chart.styles';

const DATE_FORMAT = 'MMM';
const MONTH_RANGE = 12;
const now = new Date();
const currentMonth = now.getMonth();
const currentYear = now.getFullYear();

const hasData = data => data;
const hasDate = data => data.date;
const transformDate = date => new Date(date);
const isCurrentYear = year => year === currentYear;
const isNextYear = year => year + 1 === currentYear;
const isInMonthRange = month => currentMonth - month < MONTH_RANGE;

const filterValues = marketValuesData => marketValuesData
  .filter(flow(hasData, hasDate))
  .map(({date, ...next})=> ({
    ...next,
    rawDate: date,
    date: transformDate(date),
  }))
  .filter(({date}) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    if(isCurrentYear(year)) return isInMonthRange(month);
    if(isNextYear(year)) return isInMonthRange(month + 12);
    return false
});

class PortfolioTimeChart extends PureComponent {
  static propTypes = {
  //  ratio: PropTypes.number,
    toDate: PropTypes.string,
    portfolio: PropTypes.shape({
      referenceCurrency: PropTypes.string.isRequired,
      marketValuesData: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.string,
          price: PropTypes.shape({
            value: PropTypes.number,
          }),
        })
      ).isRequired,
    }).isRequired,
  };

  static defaultProps = {
    //ratio: 1.8,
    toDate: new Date().toISOString(),
  };

  constructor(props) {
    super(props);
    this.formatDateLabel = this.formatDateLabel.bind(this);
  }

  getMarketValues() {
    return filterValues(this.props.portfolio.marketValuesData || []);
  }

  midOfMonth(date) {
    const daysToSub = getDaysInMonth(date) / 2;
    return subDays(endOfMonth(date), daysToSub);
  }

  getDateRange(mid) {
    const toDate = this.props.toDate;

    const dateRange = (dates = [], sub = MONTH_RANGE) => {
      const date = subMonths(toDate, sub - 1);
      const dateForRange = mid ? this.midOfMonth(date) : startOfMonth(date);
      const newDateArr = [...dates, dateForRange];
      return sub > 0 ? dateRange(newDateArr, sub - 1) : newDateArr;
    };

    return dateRange();
  }

  formatDateLabel(date) {
    let formtDate = format(date, DATE_FORMAT);
    let onlydate = formtDate.toUpperCase();
    return this.props.messages['app.portfolio.cumulativeChrt.' + onlydate];
  }

  addPercentage(val, perc) {
    const div = perc / 100;
    return val + val * div;
  }

  getDivisor(value) {
    let chartValue;
    if (value < 1) {
      chartValue = value * -1;
    } else {
      chartValue = value;
    }
    const divisor = (div = 0) => {
      const newDiv = div + 1;
      const result = chartValue / Math.pow(1000, newDiv);

      return result < 1 ? div : divisor(newDiv);
    };

    return value ? divisor() : 0;
  }

  getYAxisDomain(topPaddingPerc = 0) {
    const marketValues = this.getMarketValues();
    let largest = marketValues.reduce(
      (acc, marketValue) => Math.max(acc, marketValue.price.value),
      0
    );
    let smallest = marketValues.reduce(
      (acc, marketValue) => Math.min(acc, marketValue.price.value),
      0
    );
    let span = 0;
    let extendBy = 0;
    if (smallest === largest) {
      smallest = smallest * 0.8;
      largest = largest * 1.2;
    } else {
      span = largest - smallest;
      extendBy = Math.max(1, Math.pow(10, Math.floor(Math.log10(span))));
      if (smallest % extendBy !== 0) {
        smallest = Math.ceil(smallest / extendBy) * extendBy - extendBy;
      }

      if (largest % extendBy !== 0) {
        largest = Math.floor(largest / extendBy) * extendBy + extendBy;
      }
    }
    return [smallest, largest];
  }

  formatDataRange(item) {
    let divisor;
    const marketValues = this.getMarketValues();
    const smallest = marketValues.reduce(
      (acc, marketValue) => Math.min(acc, marketValue.price.value),
      0
    );
    if (item <= 0) {
      divisor = this.getDivisor(smallest * 1.2);
      return (data) => data / Math.pow(1000, divisor);
    } else {
      divisor = this.getDivisor(item);
      return (data) => data / Math.pow(1000, divisor);
    }
  }

  getyAxisLabelText(item) {
    const {
      portfolio: { referenceCurrency },
    } = this.props;
    const marketValues = this.getMarketValues();
    const largest = marketValues.reduce(
      (acc, marketValue) => Math.max(acc, marketValue.price.value),
      0
    );
    const smallest = marketValues.reduce(
      (acc, marketValue) => Math.min(acc, marketValue.price.value),
      0
    );
    let scaleIndex;
    if (item === 0) {
      scaleIndex = this.getDivisor(smallest);
    } else {
      scaleIndex = this.getDivisor(item);
    }

    const scaleLabel =
      NUMBER_SCALE[scaleIndex] && this.props.messages[NUMBER_SCALE[scaleIndex]]
        ? `${this.props.messages[NUMBER_SCALE[scaleIndex]]} `
        : '';
    return !!marketValues.length ? `(${scaleLabel + referenceCurrency})` : '';
  }

  getAxisData() {
    const xAxisTicks = this.getDateRange();
    const x2AxisTicks = this.getDateRange(true).slice(0, -1);
    const xAxisDomain = [xAxisTicks[0], xAxisTicks[MONTH_RANGE]];
    const yAxisDomain = this.getYAxisDomain(20);
    const yAxisLabelText = this.getyAxisLabelText(yAxisDomain[1]);

    const x = {
      ticks: xAxisTicks,
      domain: xAxisDomain,
    };

    const y = {
      tickFormatter: this.formatDataRange(yAxisDomain[1]),
      domain: yAxisDomain,
    };

    const x2 = {
      tickFormatter: this.formatDateLabel,
      ticks: x2AxisTicks,
      domain: xAxisDomain,
    };


    return {
      yAxisLabelText,
      axisData: { x, y, x2 },
    };
  }

  mapChartData(props) {
    const {
      portfolio: { marketValuesData, referenceCurrency },
    } = props;

    return sortBy(
      filterValues(marketValuesData),
      (marketValue) => marketValue.rawDate
    ).map((marketValue) => ({
      x: marketValue.date,
      y: marketValue.price.value,
      date: marketValue.rawDate,
      price: {
        value: marketValue.price.value,
        unit: referenceCurrency,
      },
    }));
  }

  getOuterComponents() {
    const { outerComponents } = this.props;
    const chartOuterComponents = <Interactions key="interactions" onChange={this.onInteractionChange}/>

    if (outerComponents) {
      return [...[chartOuterComponents], ...outerComponents];
    }

    return chartOuterComponents;
  }

  render() {
    const { children, ...props } = this.props;
    const { axisData, yAxisLabelText } = this.getAxisData();
    const chartData = this.mapChartData(props);

    const { margins, outerComponents, messages } = this.props;
    const marketValues = this.getMarketValues();

    const noDataMarginTop = margins && margins.top ? margins.top * -1 : 0;

    if (!marketValues.length) return (
      <NoDataChart
        key="no-data"
        marginTop={noDataMarginTop}
        messages={messages}
      />);

    return (
      <View style={styles.timechartWrapper} testID="time-chart">
        <Chart
          {...props}
          data={chartData}
          axis={axisData}
          // margins={{
          //   top: 60,
          //   left: 30,
          //   right: 20,
          //   bottom: 20,
          // }}
          margins={{
            top: 40,
            left: 174,
            right: 183,
            bottom: 25,
          }}
          paddings={{
            left: 80,
            right: 2,
            top: 0,
            bottom: 0,
          }}
          minWidth={80}
          minHeight={80}
          width = {1400}
          height = {350}
        >
          <XAxis
            showTicks={false}
            styles={{
              tickLabelColor: axisSVGStyles.color,
              lineStyle: {
                stroke: axisSVGStyles.stroke,
              },
            }}
            fontSize= {20}
            key="xaxis"
            axis="x2"
          />
          <YAxis
            labelText={yAxisLabelText}
            labelColor={axisSVGStyles.color}
            labelFontSize = {20}
            labelVerticalPadding={18}
            showLine={false}
            showTicks={false}
            styles={{
              tickLabelColor: axisSVGStyles.color,
            }}
            fontSize= {20}
            key="yaxis"
          />
          <Grid  key="grid"/>
          {!!chartData.length && (
            <LineChart key="line" color={PALETTE.lineChartColor}/>
          )}
          {this.getOuterComponents(chartData)}
          {children}
        </Chart>
      </View>
    );
  }
}

export default createContainer({
  $fragments: {
    portfolio: {
      referenceCurrency: true,
      marketValuesData: true,
    },
  },
})(PortfolioTimeChart);

export { PortfolioTimeChart };
